import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  ReactElement,
  ReactNode
} from 'react';
import { IAsset, IGetPublicAssetQuery } from '../interfaces';

interface IPublicAssetContext {
  asset?: IAsset | null | void,
  error?: unknown,
  isPublic?: boolean | null,
  loading: boolean,
  fetchPublicAsset: (assetId: string) => void
}

const PublicAssetContext = createContext<IPublicAssetContext>({
  loading: false,
  error: void 0,
  asset: void 0,
  isPublic: void 0,
  fetchPublicAsset: () => null
});

export default PublicAssetContext;

interface PublicAssetProviderProps {
  children: ReactNode;
  getPublicAsset: IGetPublicAssetQuery
}

export function PublicAssetProvider({
  children,
  getPublicAsset
}: PublicAssetProviderProps): ReactElement {
  const [asset, setAsset] = useState<IAsset | null | void>(void 0);
  const [error, setError] = useState<Error | undefined>(void 0);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchPublicAsset = useCallback(
    async (assetId: string): Promise<void> => {
      const {
        data: { getPublicAsset: publicAsset } = {},
        error: err
      } = await getPublicAsset(assetId);

      if (err) {
        setError(err);
      } else {
        setAsset(publicAsset);
      }

      setLoading(false);
    },
    [getPublicAsset, setError, setAsset, setLoading]
  );

  return (
    <PublicAssetContext.Provider
      value={{
        asset,
        loading,
        error,
        isPublic: !loading && !error && !!asset,
        fetchPublicAsset
      }}
    >
      {children}
    </PublicAssetContext.Provider>
  );
}

export function usePublicAssetContext(): IPublicAssetContext {
  return useContext(PublicAssetContext);
}
